import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";

/**
 * Base Axios instance configuration
 * Handles all HTTP requests with centralized settings
 */

// API Base URL - Configure in .env.local
// For Bridge X Global: https://offices-backend.bridge-xglobal.com
const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api";

// Create Axios instance with default config
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    authorization: `Bearer ${process.env.NEXT_PUBLIC_API_TOKEN}`,
  },
  // Enable credentials for cookie-based auth
  withCredentials: false,
});

/**
 * Request Interceptor
 * Runs before every request is sent
 */
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add language header (useful for i18n APIs)
    const language =
      typeof window !== "undefined"
        ? localStorage.getItem("language") || "en"
        : "en";

    if (config.headers) {
      config.headers["Accept-Language"] = language;
    }

    // Log request in development
    if (process.env.NODE_ENV === "development") {
      console.log("🚀 API Request:", {
        method: config.method?.toUpperCase(),
        url: config.url,
        params: config.params,
      });
    }

    return config;
  },
  (error: AxiosError) => {
    console.error("❌ Request Error:", error);
    return Promise.reject(error);
  },
);

/**
 * Response Interceptor
 * Runs after every response is received
 */
axiosInstance.interceptors.response.use(
  (response) => {
    // Log response in development
    if (process.env.NODE_ENV === "development") {
      console.log("✅ API Response:", {
        status: response.status,
        url: response.config.url,
        data: response.data,
      });
    }

    return response;
  },
  async (error: AxiosError) => {
    // Log error details in development
    if (process.env.NODE_ENV === "development") {
      console.error("❌ API Error:", {
        status: error.response?.status,
        url: error.config?.url,
        message: error.message,
        data: error.response?.data,
      });
    }

    // Handle different error scenarios
    if (error.response) {
      const { status } = error.response;

      // Handle 404 Not Found
      if (status === 404) {
        if (process.env.NODE_ENV === "development") {
          console.warn("⚠️ Resource not found");
        }
      }

      // Handle 500 Server Error
      if (status >= 500) {
        if (process.env.NODE_ENV === "development") {
          console.error("🔥 Server error - please try again later");
        }
      }
    } else if (error.request) {
      // Request made but no response received (network error)
      if (process.env.NODE_ENV === "development") {
        console.error("🌐 Network Error - No response received");
      }
    } else {
      // Error in request setup
      if (process.env.NODE_ENV === "development") {
        console.error("⚙️ Request Setup Error:", error.message);
      }
    }

    // Always reject with the original error
    return Promise.reject(error);
  },
);

export default axiosInstance;
